import { Alert, Button, Modal, Tabs, Tooltip } from 'antd';
import { ReactComponent as PythonSVG } from '../Icons/python.svg';
import { ReactComponent as PhpSVG } from '../Icons/php.svg';
import { CodeOutlined, LinkOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import Icon, { CopyOutlined } from '@ant-design/icons';
import { infoNotification, showNotification } from '../Notifications';
import recipe_styles from '../Recipe/recipe.module.sass';
import { loadAPIAccess } from '../../redux/actions/APIAccess';
import { useDispatch, useSelector } from 'react-redux';
import { STATUS } from '../../redux/constants';
import moment from 'moment-timezone';
import styles from './copy-endpoint.module.sass';
import history from '../../redux/history';
import { recursiveReplace } from '../../redux/utils/utils';

export const CopyEndpointContent = ({
  handleShowAutomateExportModal,
  endpoint,
  body,
  description,
  fpuuid,
  exportFormat,
}) => {
  const PythonIcon = (props) => <Icon component={PythonSVG} {...props} />;
  const PHPIcon = (props) => <Icon component={PhpSVG} {...props} />;
  const dispatch = useDispatch();
  const apiAccess = useSelector((state) => state?.APIAccess);
  const [apiKey, setApiKey] = useState({ api_key: '<YOUR API KEY>' });
  const [activeTab, setActiveTab] = useState('url');
  const [filterProfileUUID, setFilterProfileUUID] = useState(fpuuid);
  const [_endpoint, _setEndpoint] = useState(endpoint);
  const [snippets, setSnippets] = useState({});

  useEffect(() => {
    dispatch(loadAPIAccess(true));
  }, []);

  useEffect(() => {
    setFilterProfileUUID(fpuuid);
  }, [fpuuid]);

  useEffect(() => {
    if (apiAccess.APIAccessStatus == STATUS.LOADED) {
      setApiKey(
        apiAccess.APIAccessData?.find(
          (apiKey) => moment(apiKey.expires_at)?.diff(moment(), 'days') > 0,
        ) || { api_key: '<YOUR API KEY>' },
      );
    }
  }, [apiAccess?.APIAccessStatus]);

  useEffect(() => {
    //TODO: when filter applied, we need the threat-ranking endpoint to post data, messy workaround :( needs a tender touch
    let url = new URL(
      endpoint?.replace('api/v1/iocs', 'api/v1/threat-ranking'),
    );
    if (filterProfileUUID) {
      url.searchParams.append('filter_profile', filterProfileUUID);
    }
    for (const [k, v] of url.searchParams.entries()) {
      if (v === '[object Object]' || v === 'undefined') {
        url.searchParams.delete(k);
      }
    }
    _setEndpoint(url?.href);
  }, [filterProfileUUID, endpoint]);

  useEffect(() => {
    setSnippets({
      url: _endpoint,
      curl:
        `curl -L "${_endpoint}" --header "x-api-key:${apiKey?.api_key}"` +
        (body
          ? ` --header 'Content-Type: application/json' -d '${JSON.stringify(
              body,
            )}'`
          : ''),
      python:
        `import requests
data = requests.get('${_endpoint}', headers={'x-api-key': '${apiKey?.api_key}'` +
        (body
          ? `, 'Content-Type': 'application/json'}, json=${JSON.stringify(body)
              .replaceAll('null', 'None')
              .replaceAll('true', 'True')
              .replaceAll('false', 'False')}`
          : '}') +
        `)
print(data.content)`,
      php:
        `$ch = curl_init('${_endpoint}');
curl_setopt($ch, CURLOPT_HTTPHEADER, ['x-api-key: ${apiKey?.api_key}', ` +
        (body
          ? `'Content-type: application/json']);
curl_setopt($ch, CURLOPT_POSTFIELDS, '${JSON.stringify(body)}');`
          : ']);') +
        `
curl_setopt($ch, CURLOPT_FOLLOWLOCATION, true);
$response = curl_exec($ch);
var_dump($response);
curl_close($ch);`,
      javascript: `const request = new XMLHttpRequest();
request.onreadystatechange = function() {
if (this.readyState == 4 && this.status == 200) {
 console.log(request.responseText);
}
};
request.open('GET', '${_endpoint}');
request.setRequestHeader('x-api-key', '${apiKey?.api_key}');
request.send();`,
    });
  }, [_endpoint, body, apiKey]);

  const alert = (
    <Alert
      message="Seems like you don't have a valid API key, create one in your Organisation menu or click on the button beside"
      type="warning"
      action={
        <Button
          size={'small'}
          type={'ghost'}
          onClick={() => history.push('/organization')}
        >
          API Keys
        </Button>
      }
    />
  );

  const tab_items = [
    {
      label: <LinkOutlined />,
      key: 'url',
      children: (
        <>
          {body && (
            <Alert
              message="Unsaved changes in the filter profile won't be included"
              type="warning"
            />
          )}
          <pre className={styles.code_snippet}>{snippets.url}</pre>
        </>
      ),
    },
    {
      label: <CodeOutlined />,
      key: 'curl',
      children: (
        <>
          {apiKey?.api_key == '<YOUR API KEY>' && alert}
          <pre className={styles.code_snippet}>{snippets.curl}</pre>
        </>
      ),
    },
    {
      label: <PythonIcon />,
      key: 'python',
      children: (
        <>
          {apiKey?.api_key == '<YOUR API KEY>' && alert}
          <pre className={styles.code_snippet}>{snippets.python}</pre>
        </>
      ),
    },
    {
      label: <PHPIcon />,
      key: 'php',
      children: (
        <>
          {apiKey?.api_key == '<YOUR API KEY>' && alert}
          <pre className={styles.code_snippet}>{snippets.php}</pre>
        </>
      ),
    },
  ];

  const copyToClipboard = () => {
    navigator.clipboard.writeText(snippets[activeTab] || snippets.url);
    showNotification(
      infoNotification({
        statusCode: 100,
        message: 'Copied to clipboard!',
      }),
    );
  };

  return (
    <>
      <p>{description}</p>
      {handleShowAutomateExportModal && (
        <>
          <Tabs
            items={tab_items}
            defaultActiveKey={'url'}
            type={'card'}
            onTabClick={(tab_key) => setActiveTab(tab_key)}
          />
          <Button
            data-cy="copyEndpointActionButton"
            onClick={copyToClipboard}
            disabled={
              typeof exportFormat === 'string'
                ? exportFormat?.length > 0
                  ? false
                  : true
                : false
            }
            type={'primary'}
            icon={<CopyOutlined />}
            style={{ marginTop: '10px' }}
          >
            {activeTab == 'url' ? 'Copy API Endpoint' : 'Copy Code Snippet'}
          </Button>
        </>
      )}
    </>
  );
};

export const CopyEndpoint = (props) => {
  const {
    endpoint,
    body,
    title,
    description,
    visible,
    handleShowAutomateExportModal,
    handleShowDownloadModal,
    fpuuid,
    centered,
    exportFormat,
  } = props;

  const True = 'True';
  const False = 'False';
  const None = 'None';
  const replacements = [
    { find: 'false', replace: 'False' },
    { find: 'true', replace: 'True' },
    { find: true, replace: True },
    { find: false, replace: False },
    { find: null, replace: None },
  ];
  recursiveReplace(body, replacements);

  return (
    <Modal
      centered={centered}
      title={title}
      open={visible}
      width={'624px'}
      footer={
        <Button
          data-cy="copyEndpointCloseButton"
          className={recipe_styles.ghost_default_button}
          onClick={() => {
            if (handleShowAutomateExportModal) {
              handleShowAutomateExportModal(false);
            }
            if (handleShowDownloadModal) {
              handleShowDownloadModal(false);
            }
          }}
          ghost={true}
        >
          Close
        </Button>
      }
      onCancel={() => {
        if (handleShowAutomateExportModal) {
          handleShowAutomateExportModal(false);
        }
        if (handleShowDownloadModal) {
          handleShowDownloadModal(false);
        }
      }}
    >
      <CopyEndpointContent
        handleShowAutomateExportModal={handleShowAutomateExportModal}
        fpuuid={fpuuid}
        endpoint={endpoint}
        body={body}
        description={description}
        exportFormat={exportFormat}
      />
    </Modal>
  );
};

export default CopyEndpoint;
